import React, { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { KNOWN_SUPABASE_ICONS, SEMANTIC_ICON_MAP } from '@/utils/SupabaseIconWorkaround';

interface MissingIcon {
  semantic: string;
  filename: string;
  category: string;
  description: string;
  aiPrompt: string;
}

export const IconUploadManager: React.FC = () => {
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [dragOver, setDragOver] = useState(false);

  // Generate missing icons suggestions
  const missingIcons: MissingIcon[] = [
    {
      semantic: 'atlas',
      filename: 'atlas-avatar.png',
      category: 'avatar',
      description: 'Atlas AI Strategy Advisor avatar',
      aiPrompt: 'Create a professional AI avatar for Atlas, a strategic thinking and problem-solving AI mentor. Should be masculine, wise-looking, with strategic/analytical elements. Blue gradient color scheme. Circular icon format, similar to Lyra avatar style.'
    },
    {
      semantic: 'maya',
      filename: 'maya-avatar.png', 
      category: 'avatar',
      description: 'Maya AI Communication Expert avatar',
      aiPrompt: 'Create a professional AI avatar for Maya, a communication and voice expert AI mentor. Should be feminine, friendly and articulate-looking, with communication/speech elements. Blue gradient color scheme. Circular icon format, similar to Lyra avatar style.'
    },
    {
      semantic: 'workflow',
      filename: 'workflow-process.png',
      category: 'ui',
      description: 'Workflow and process management icon',
      aiPrompt: 'Create a clean workflow/process icon showing connected steps or flowchart elements. Blue gradient style, minimalist design, suitable for UI. 64x64px, transparent background.'
    },
    {
      semantic: 'network',
      filename: 'network-connection.png',
      category: 'ui', 
      description: 'Network and connection icon',
      aiPrompt: 'Create a network connection icon with connected nodes or network mesh. Blue gradient style, tech-focused, clean design. 64x64px, transparent background.'
    },
    {
      semantic: 'settings',
      filename: 'settings-gear.png',
      category: 'ui',
      description: 'Settings and configuration icon',
      aiPrompt: 'Create a settings gear icon, modern and clean. Blue gradient style, minimalist. 64x64px, transparent background.'
    },
    {
      semantic: 'notification',
      filename: 'notification-bell.png',
      category: 'ui',
      description: 'Notification and alerts icon', 
      aiPrompt: 'Create a notification bell icon, clean and modern. Blue gradient style, suitable for UI notifications. 64x64px, transparent background.'
    },
    {
      semantic: 'calendar',
      filename: 'calendar-schedule.png',
      category: 'ui',
      description: 'Calendar and scheduling icon',
      aiPrompt: 'Create a calendar icon showing dates/schedule. Blue gradient style, clean and professional. 64x64px, transparent background.'
    },
    {
      semantic: 'document',
      filename: 'document-file.png', 
      category: 'ui',
      description: 'Document and file icon',
      aiPrompt: 'Create a document/file icon, clean and modern. Blue gradient style, representing text documents or files. 64x64px, transparent background.'
    }
  ];

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    setUploadStatus('');

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          throw new Error(`${file.name} is not an image file`);
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          throw new Error(`${file.name} is too large (max 5MB)`);
        }

        // Upload to Supabase storage
        const { data, error } = await supabase.storage
          .from('app-icons')
          .upload(file.name, file, {
            cacheControl: '3600',
            upsert: true // Overwrite if exists
          });

        if (error) throw error;

        return {
          filename: file.name,
          path: data.path,
          success: true
        };
      });

      const results = await Promise.allSettled(uploadPromises);
      
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected');

      let statusMessage = `✅ Successfully uploaded ${successful}/${files.length} files`;
      
      if (failed.length > 0) {
        const errors = failed.map(r => 
          r.status === 'rejected' ? r.reason.message : 'Unknown error'
        );
        statusMessage += `\n❌ Errors: ${errors.join(', ')}`;
      }

      setUploadStatus(statusMessage);

      // Refresh the page after successful upload
      if (successful > 0) {
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }

    } catch (error: any) {
      setUploadStatus(`❌ Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileUpload(e.dataTransfer.files);
  }, []);

  const copyPromptToClipboard = (prompt: string, iconName: string) => {
    navigator.clipboard.writeText(prompt);
    setUploadStatus(`✅ Copied AI prompt for ${iconName} to clipboard!`);
  };

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <div className="p-6 bg-white rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-400 transition-colors">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">📤 Upload New Icons</h3>
        
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`p-8 rounded-lg border-2 border-dashed transition-all ${
            dragOver 
              ? 'border-blue-500 bg-blue-50' 
              : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
          }`}
        >
          <div className="text-center">
            <div className="text-4xl mb-4">🎨</div>
            <p className="text-gray-600 mb-4">
              Drag and drop image files here, or click to browse
            </p>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileUpload(e.target.files)}
              className="hidden"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              className="inline-block px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
            >
              Browse Files
            </label>
            <p className="text-xs text-gray-500 mt-2">
              Supported formats: PNG, JPG, SVG, WebP (Max 5MB each)
            </p>
          </div>
        </div>

        {uploading && (
          <div className="mt-4 flex items-center space-x-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            <span className="text-blue-600">Uploading to Supabase...</span>
          </div>
        )}

        {uploadStatus && (
          <div className={`mt-4 p-3 rounded-lg ${
            uploadStatus.includes('✅') 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            <pre className="text-sm whitespace-pre-wrap">{uploadStatus}</pre>
          </div>
        )}
      </div>

      {/* Missing Icons Suggestions */}
      <div className="p-6 bg-yellow-50 rounded-lg border border-yellow-200">
        <h3 className="text-lg font-semibold text-yellow-800 mb-4">🤖 Missing Icons - AI Creation Prompts</h3>
        <p className="text-yellow-700 mb-4">
          Here are suggested icons you might want to create using your AI image maker:
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {missingIcons.map((icon) => (
            <div key={icon.semantic} className="p-4 bg-white rounded-lg border border-yellow-300">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h4 className="font-semibold text-gray-800">{icon.filename}</h4>
                  <p className="text-sm text-gray-600">
                    Semantic: <code className="text-blue-600">{icon.semantic}</code> | 
                    Category: <span className="text-purple-600 ml-1">{icon.category}</span>
                  </p>
                </div>
                <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                  Missing
                </span>
              </div>
              
              <p className="text-sm text-gray-700 mb-3">{icon.description}</p>
              
              <div className="space-y-2">
                <div className="bg-gray-50 p-3 rounded text-xs">
                  <strong>AI Prompt:</strong>
                  <p className="mt-1 text-gray-700">{icon.aiPrompt}</p>
                </div>
                
                <button
                  onClick={() => copyPromptToClipboard(icon.aiPrompt, icon.filename)}
                  className="w-full text-xs bg-yellow-100 text-yellow-800 py-2 px-3 rounded hover:bg-yellow-200 transition-colors"
                >
                  📋 Copy AI Prompt
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Instructions */}
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h3 className="text-blue-800 font-semibold mb-2">📋 Instructions</h3>
        <ul className="text-blue-700 text-sm space-y-1">
          <li>• Upload images directly to your Supabase app-icons bucket</li>
          <li>• Use the AI prompts above to create missing icons consistently</li>
          <li>• Icons will automatically appear in the gallery after upload</li>
          <li>• Recommended size: 64x64px or 128x128px for best quality</li>
          <li>• Use PNG format with transparent backgrounds when possible</li>
        </ul>
      </div>
    </div>
  );
};

export default IconUploadManager;